/**
 * ServiceOrderSettingsTab - Aba "Ordem de Serviço" das Configurações do tenant
 *
 * Liga/desliga o módulo Ordem de Serviço. Somente administradores;
 * a validação real acontece na RPC.
 *
 * RELEASE: 2026-10-13 v1.2.0 - Chegada ao local: raio, precisão máxima, política fora do raio e foto.
 * RELEASE: 2026-10-12 v1.1.0 - Seção Portal do Prestador (liga/desliga e link do tenant).
 * RELEASE: 2026-10-08 v1.0.0 - Criação inicial (Fase 1 da OS).
 */
import { useEffect, useState } from 'react';
import { Copy, MapPin, Smartphone, Wrench } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { providerPortalUrl, useProviderPortalSettings } from '@/hooks/useProviderPortalAdmin';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { SettingsCard, SettingsSectionHeader } from '@/components/tenant/SettingsInfoRow';
import { useServiceOrderModule } from '@/hooks/useServiceOrderModule';

export function ServiceOrderSettingsTab() {
  const { isEnabled, isLoading, toggleModule } = useServiceOrderModule();
  if (isLoading) return <Skeleton className="h-40 w-full rounded-lg" />;

  return (
    <SettingsCard>
      <SettingsSectionHeader
        eyebrow="Módulo"
        title="Ordem de Serviço"
        description="Controle de ordens de serviço com tipos de serviço, categorias, fases, agenda do prestador e checklists."
      />
      <div className="flex items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-start gap-3">
          <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">Habilitar módulo Ordem de Serviço</p>
            <p className="text-muted-foreground">Desligar esconde o menu, mas nenhum cadastro é apagado.</p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          disabled={toggleModule.isPending}
          onCheckedChange={(v) => toggleModule.mutate(v)}
          aria-label="Habilitar módulo Ordem de Serviço"
        />
      </div>
      <ProviderPortalSection />
      <ArrivalSettingsSection />
    </SettingsCard>
  );
}

/** Portal do Prestador: app do prestador em link exclusivo do tenant (/prestador/:slug). */
function ProviderPortalSection() {
  const { settings, toggle } = useProviderPortalSettings();
  const url = settings?.slug ? providerPortalUrl(settings.slug) : '';
  return (
    <div className="space-y-3 border-t px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">Portal do Prestador</p>
            <p className="text-muted-foreground">Prestadores liberados acessam suas O.S. pelo celular. Libere cada um em Pessoas › Prestadores › Acesso ao portal.</p>
          </div>
        </div>
        <Switch checked={!!settings?.enabled} disabled={toggle.isPending} onCheckedChange={(v) => toggle.mutate(v)} aria-label="Habilitar Portal do Prestador" />
      </div>
      {settings?.enabled && url && (
        <div className="flex items-center gap-2 pl-7 text-xs">
          <code className="truncate rounded bg-muted px-2 py-1">{url}</code>
          <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Copiar link"
            onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copiado'); }}><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      )}
    </div>
  );
}

/** Chegada ao local: regras aplicadas pelo servidor quando o prestador confirma a chegada. */
function ArrivalSettingsSection() {
  const { settings, updateArrival } = useProviderPortalSettings();
  const [radius, setRadius] = useState('150');
  const [accuracy, setAccuracy] = useState('100');
  useEffect(() => {
    if (settings?.arrival_radius_m) setRadius(String(settings.arrival_radius_m));
    if (settings?.arrival_max_accuracy_m) setAccuracy(String(settings.arrival_max_accuracy_m));
  }, [settings?.arrival_radius_m, settings?.arrival_max_accuracy_m]);
  if (!settings?.enabled) return null;
  // Salva ao sair do campo, somente se o valor for válido e diferente do atual.
  const saveNumber = (value: string, min: number, max: number, current: number | undefined, key: 'radius' | 'accuracy') => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < min || n > max) { toast.error(`Informe um valor entre ${min} e ${max} metros`); return; }
    if (n !== current) updateArrival.mutate({ [key]: n });
  };
  return (
    <div className="space-y-4 border-t px-6 py-4">
      <div className="flex items-start gap-3">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="space-y-1 text-sm">
          <p className="font-medium text-foreground">Chegada ao local</p>
          <p className="text-muted-foreground">Compara o GPS do prestador com as coordenadas informadas na OS.</p>
        </div>
      </div>
      <div className="grid gap-4 pl-7 sm:grid-cols-2">
        <label className="space-y-1 text-sm"><span className="text-muted-foreground">Raio de chegada (metros)</span>
          <Input type="number" inputMode="numeric" min={20} max={2000} value={radius} disabled={updateArrival.isPending}
            onChange={(e) => setRadius(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onBlur={() => saveNumber(radius, 20, 2000, settings.arrival_radius_m, 'radius')} /></label>
        <label className="space-y-1 text-sm"><span className="text-muted-foreground">Precisão máxima do GPS (metros)</span>
          <Input type="number" inputMode="numeric" min={10} max={500} value={accuracy} disabled={updateArrival.isPending}
            onChange={(e) => setAccuracy(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={() => saveNumber(accuracy, 10, 500, settings.arrival_max_accuracy_m, 'accuracy')} /></label>
        <label className="space-y-1 text-sm"><span className="text-muted-foreground">Fora do raio</span>
          <Select value={settings.arrival_out_of_radius ?? 'justify'} disabled={updateArrival.isPending}
            onValueChange={(v) => updateArrival.mutate({ outOfRadius: v as 'justify' | 'block' })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="justify">Permitir com justificativa</SelectItem>
              <SelectItem value="block">Bloquear confirmação</SelectItem>
            </SelectContent>
          </Select></label>
        <div className="flex items-center justify-between gap-3 text-sm sm:pt-6">
          <span className="text-muted-foreground">Foto da fachada obrigatória</span>
          <Switch checked={settings.arrival_photo_required ?? true} disabled={updateArrival.isPending}
            onCheckedChange={(v) => updateArrival.mutate({ photoRequired: v })} aria-label="Foto da fachada obrigatória" />
        </div>
      </div>
    </div>
  );
}
